/**
 * Verdeelt de beschikbare hoogte in de weergave 'Lijst en agenda' over de
 * agenda (boven) en de tabel met huishoudens (eronder).
 *
 * WAAROM ÉÉN BEREKENING VOOR ALLEBEI: eerst leidde ik de hoogte van de agenda
 * af uit de gemeten hoogte van de tabel. Dat is rond: de tabel is zo hoog als
 * hij mag zijn, en hoe hoog dat is hangt af van wat de agenda overlaat. Met een
 * kaartenlijst viel dat niet op (die was altijd laag); met een echte tabel wel —
 * de tabel werd 333px en het geheel stak 100px onder het scherm uit, en daar is
 * geen schuifbalk voor: de schil van deze pagina scrolt niet. Nu bepaalt deze
 * functie allebei de hoogten uit dezelfde ruimte, in één richting.
 *
 * WAAROM GETALLEN EN GEEN 100%: tussen het blok om de agenda en FullCalendar
 * zitten drie divs van `CalendarFilters` zonder opgegeven hoogte. `height="100%"`
 * lost daar op naar 0, en dan tekent FullCalendar de kop en de 'Hele dag'-regel
 * wél en het tijdrooster niet — een lege witte balk, zonder fout.
 * AI-CONTEXT: See docs/pitfalls.md#ui-chart-renders-blank-after-move
 */

/** Hieronder is er geen bruikbaar stuk dag meer te zien. FullCalendar schuift zelf binnenin. */
const MIN_CALENDAR = 180

/** Hieronder past er geen enkele tabelrij meer onder de koprij. */
const MIN_LIST = 120

/** Wat de agenda krijgt van de ruimte die na de vaste onderdelen overblijft. */
const CALENDAR_SHARE = 0.62

/**
 * Speling voor afronding. Randen en halve pixels tellen niet altijd rond op, en
 * gemeten kwam het geheel op 901px uit bij een venster van 900 — één pixel, maar
 * genoeg voor een schuifbalk op een pagina die niet hoort te schuiven.
 */
const SAFETY = 4

/**
 * @typedef {object} AgendaPanes
 * @property {number} calendarHeight - Hoogte voor FullCalendar zelf, in pixels
 * @property {number} listMaxHeight - Maximale hoogte van het schuifvak van de tabel, in pixels
 *
 * @param {object} maten - Alles gemeten in de browser; jsdom doet geen layout
 * @param {number} maten.viewportHeight - `window.innerHeight`
 * @param {number} maten.topOffset - Afstand van de bovenkant van het scherm tot dit blok
 * @param {number} [maten.calendarChrome] - Filterbalk + padding boven de kalender in haar blok
 * @param {number} [maten.listChrome] - Kop, uitleg en padding boven het schuifvak van de tabel
 * @param {number} [maten.gap] - Ruimte tussen de twee blokken
 * @param {number} [maten.calendarShare] - Het aandeel van de agenda (0–1) zoals de planner het met de scheidslijn koos; anders CALENDAR_SHARE
 * @returns {AgendaPanes}
 */
const splitAgendaPanes = ({
    viewportHeight,
    topOffset,
    calendarChrome,
    listChrome,
    gap = 14,
    calendarShare,
} = {}) => {
    const getal = (x) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : null)

    const scherm = getal(viewportHeight)
    // Zonder schermhoogte valt er niets te verdelen; dan allebei de ondergrens,
    // want die is in elk geval zichtbaar. 0 zou een lege balk opleveren.
    if (scherm === null || scherm === 0) {
        return { calendarHeight: MIN_CALENDAR, listMaxHeight: MIN_LIST }
    }

    const vast = (getal(topOffset) ?? 0)
        + (getal(calendarChrome) ?? 0)
        + (getal(listChrome) ?? 0)
        + (getal(gap) ?? 0)

    const teVerdelen = scherm - vast - SAFETY

    // Past het niet, dan allebei de ondergrens: liever twee krappe blokken die
    // in beeld staan dan één ruim blok waarvan de helft onbereikbaar is.
    if (teVerdelen < MIN_CALENDAR + MIN_LIST) {
        return { calendarHeight: MIN_CALENDAR, listMaxHeight: MIN_LIST }
    }

    // De keuze van de planner, anders de standaard. De ondergrenzen winnen van
    // de keuze: hoe ver je de scheidslijn ook sleept, allebei blijven zichtbaar.
    const aandeel = typeof calendarShare === 'number' && Number.isFinite(calendarShare)
        ? calendarShare
        : CALENDAR_SHARE
    const calendarHeight = Math.min(
        teVerdelen - MIN_LIST,
        Math.max(MIN_CALENDAR, Math.floor(teVerdelen * aandeel)),
    )

    return {
        calendarHeight,
        listMaxHeight: Math.max(MIN_LIST, Math.floor(teVerdelen - calendarHeight)),
    }
}

export { MIN_CALENDAR, MIN_LIST, CALENDAR_SHARE, SAFETY }
export default splitAgendaPanes
